import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Container, Grid, Typography } from '@mui/material';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import HeroSection from '../components/marketing/HeroSection';
import FeatureShowcase from '../components/marketing/FeatureShowcase';
import FeatureCard from '../components/marketing/FeatureCard';
import CTASection from '../components/marketing/CTASection';
import Reveal from '../components/marketing/Reveal';

const Landing: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      const target = user.role === 'superadmin' ? '/superadmin' : '/dashboard';
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const features = [
    {
      key: 'caseManagement',
      icon: <GavelOutlinedIcon fontSize="inherit" />,
    },
    {
      key: 'clientDatabase',
      icon: <PeopleAltOutlinedIcon fontSize="inherit" />,
    },
    {
      key: 'accounting',
      icon: <PaymentsOutlinedIcon fontSize="inherit" />,
    },
    {
      key: 'documentManagement',
      icon: <FolderOutlinedIcon fontSize="inherit" />,
    },
  ];

  const showcases = [
    {
      key: 'crearCausa',
      eyebrow: t('marketing.landing.showcase.crearCausa.eyebrow'),
      title: t('marketing.landing.showcase.crearCausa.title'),
      description: t('marketing.landing.showcase.crearCausa.description'),
      imageSrc: '/screenshots/2-crear-causa.jpeg',
    },
    {
      key: 'crearClientes',
      eyebrow: t('marketing.landing.showcase.crearClientes.eyebrow'),
      title: t('marketing.landing.showcase.crearClientes.title'),
      description: t('marketing.landing.showcase.crearClientes.description'),
      imageSrc: '/screenshots/3-crear-clientes.jpeg',
    },
    {
      key: 'llevaTusPagos',
      eyebrow: t('marketing.landing.showcase.llevaTusPagos.eyebrow'),
      title: t('marketing.landing.showcase.llevaTusPagos.title'),
      description: t('marketing.landing.showcase.llevaTusPagos.description'),
      imageSrc: '/screenshots/4-lleva-tus-pagos.jpeg',
    },
    {
      key: 'registraTusGastos',
      eyebrow: t('marketing.landing.showcase.registraTusGastos.eyebrow'),
      title: t('marketing.landing.showcase.registraTusGastos.title'),
      description: t('marketing.landing.showcase.registraTusGastos.description'),
      imageSrc: '/screenshots/5-registra-tus-gastos.jpeg',
    },
    {
      key: 'anotaTusPlazos',
      eyebrow: t('marketing.landing.showcase.anotaTusPlazos.eyebrow'),
      title: t('marketing.landing.showcase.anotaTusPlazos.title'),
      description: t('marketing.landing.showcase.anotaTusPlazos.description'),
      imageSrc: '/screenshots/6-anota-tus-plazos.jpeg',
    },
  ];

  return (
    <Box>
      <HeroSection
        badge={t('marketing.landing.hero.badge')}
        title={t('marketing.landing.hero.title')}
        subtitle={t('marketing.landing.hero.subtitle')}
        primaryCta={{
          label: t('marketing.landing.hero.ctaPrimary'),
          path: '/register',
        }}
        trustBullets={[
          t('marketing.landing.hero.bullets.freeAccount'),
          t('marketing.landing.hero.bullets.noCard'),
          t('marketing.landing.hero.bullets.secureData'),
        ]}
        heroImage={{
          src: '/screenshots/1-dashboard.jpeg',
          alt: t('marketing.landing.showcase.dashboard.title'),
        }}
      />

      <Box sx={{ py: { xs: 6, md: 10 }, borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  letterSpacing: '-0.01em',
                }}
              >
                {t('marketing.landing.features.title')}
              </Typography>
              <Typography
                variant="h6"
                component="p"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  maxWidth: 640,
                  mx: 'auto',
                }}
              >
                {t('marketing.landing.features.subtitle')}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {features.map((feature) => (
                <Grid key={feature.key} size={{ xs: 12, sm: 6, md: 3 }}>
                  <FeatureCard
                    icon={feature.icon}
                    title={t(`marketing.landing.features.${feature.key}.title`)}
                    description={t(`marketing.landing.features.${feature.key}.description`)}
                  />
                </Grid>
              ))}
            </Grid>
          </Reveal>
        </Container>
      </Box>

      {showcases.map((showcase, index) => (
        <FeatureShowcase
          key={showcase.key}
          eyebrow={showcase.eyebrow}
          title={showcase.title}
          description={showcase.description}
          imageSrc={showcase.imageSrc}
          imageAlt={showcase.title}
          reverse={index % 2 === 1}
          backgroundColor={index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : '#ffffff'}
        />
      ))}

      <CTASection
        variant="dark"
        title={t('marketing.landing.cta.title')}
        subtitle={t('marketing.landing.cta.subtitle')}
        buttonLabel={t('marketing.landing.cta.button')}
        buttonPath="/register"
      />
    </Box>
  );
};

export default Landing;
